import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	OutgoingLinksQuery,
	type OutgoingLinksQueryData,
	type OutgoingLinksQueryVariables,
} from '@confluence/connectors/entry-points/preloadOutgoingLinks.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getThirdPartyPrompt, type ThirdPartyPromptResult } from './getThirdPartyPrompt.ts';

const NO_PROMPT: ThirdPartyPromptResult = {
	state: { kind: 'none' },
	count: 0,
	diagnostics: {
		connectorRowsFailureReason: null,
		connectorRowCount: 0,
		candidateCount: 0,
		addressCount: 0,
		unmatchedAddressCount: 0,
	},
};

const readAddresses = (data: OutgoingLinksQueryData, contentId: string): string[] | undefined => {
	const nodes = data.content?.nodes;
	const currentPage = nodes?.find((node) => node?.id === contentId);

	if (!currentPage && nodes?.length) {
		return undefined;
	}

	return (
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		currentPage?.outgoingLinks?.externalOutgoingLinks?.nodes
			?.map((node) => node?.url)
			?.filter((url): url is string => Boolean(url)) ?? []
	);
};

/** Whether a connect prompt can run for this page. */
export const useThirdPartyPromptState = (contentId: string): ThirdPartyPromptResult => {
	const { cloudId, userId } = useSessionData();

	const { data, error, loading } = useQueryInterop<
		OutgoingLinksQueryData,
		OutgoingLinksQueryVariables
	>(OutgoingLinksQuery, {
		variables: { contentId },
		skip:
			!contentId ||
			!userId ||
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			!UNSAFE_expValEqualsNoExposure('confluence_3p_in_related_content', 'shouldEvaluate', true),
	});
	const pageAddresses = data ? readAddresses(data, contentId) : undefined;

	if (
		!contentId ||
		!userId ||
		error ||
		(!loading && data !== undefined && pageAddresses === undefined) ||
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		!UNSAFE_expValEqualsNoExposure('confluence_3p_in_related_content', 'shouldEvaluate', true)
	) {
		return NO_PROMPT;
	}

	// Missing data is not "this page has no links". That would paint recs and then push them
	// down when a candidate arrives. Hold until the query has a result, matching SSR.
	return getThirdPartyPrompt({
		userId,
		cloudId,
		pageAddresses,
	});
};
